/*
 * Campaign Analysis campaign DISCOVERY.
 *
 * Two contracts live on this page and they had been collapsed into one.
 *
 *   Selector eligibility — does this campaign EXIST for the selected
 *   advertiser, and is this viewer permitted to see it?
 *
 *   Analytics state — once selected, what does this campaign resolve to?
 *   Live delivery, pre-live, no delivery reported, reporting unavailable,
 *   governance blocked.
 *
 * Campaign Analysis built its dropdown from `resolveCanonicalCampaigns({
 * liveCampaigns, importedReportingCampaigns })` — ad-platform identities and
 * imported REPORTING_ONLY rows. Governed media campaigns were ENRICHMENT ONLY
 * in that call, so a campaign the advertiser genuinely owns was invisible
 * unless some provider had already minted an id for it or some reporting row
 * already carried its delivery. Analytics readiness was deciding campaign
 * EXISTENCE.
 *
 * For Never Stop Chasing (advertiser 16) that meant a selector with a single
 * entry. Campaign 44 has provider delivery; Campaign 50 — approved, flighted
 * 2026-08-21 → 2026-08-30, simply not yet delivering — had no platform
 * identity and no imported reporting row, so it did not exist as far as the
 * dropdown was concerned. There is nothing wrong with Campaign 50; there was
 * something wrong with asking delivery whether a campaign exists.
 *
 * The order is:
 *
 *   campaign belongs to advertiser → show in selector → select campaign →
 *   resolve THAT campaign's analytics state
 *
 * The backend already publishes the canonical advertiser → campaign
 * relationship at GET /api/media-campaigns?advertiserId=N&reportingScope=1,
 * which applies the two authorities that DO gate existence — the viewer's
 * advertiser scope and the internal supervised-flight artifact exclusion — and
 * nothing else. That response IS the selector's authority. This module adds no
 * second eligibility rule on top of it, because every rule it could add
 * (approved, in flight, delivering, mapped, snapshotted, previously analysed)
 * is an analytics-state question wearing an existence question's clothes.
 *
 * ONE implementation, byte-identical in Cortex and Quantum. Presentation may
 * differ by portal role; which campaigns exist may not.
 */

import {
  isInternalReportingExcludedCampaign,
  resolveCanonicalCampaigns,
} from "./canonicalCampaignIdentity";

export const CAMPAIGN_ANALYSIS_GOVERNED_CAMPAIGNS_PATH = "/api/media-campaigns";

/*
 * The canonical advertiser → campaign request. `reportingScope=1` is what asks
 * the backend to apply the reporting/optimizer exclusion server-side for every
 * viewer, so the selector and the campaign metrics agree on what a real
 * campaign is.
 */
export const buildCampaignAnalysisGovernedCampaignsPath = (advertiserId) =>
  `${CAMPAIGN_ANALYSIS_GOVERNED_CAMPAIGNS_PATH}?advertiserId=${encodeURIComponent(
    String(advertiserId ?? "")
  )}&reportingScope=1`;

/*
 * Selector eligibility, in full.
 *
 * A governed row the advertiser-scoped canonical list returned is a campaign of
 * this advertiser's, so it belongs in the selector. The only rejection is the
 * internal supervised-flight authorization/proof artifact — not a campaign at
 * all — which is re-checked client-side purely so a response fetched without
 * `reportingScope=1` can never introduce one.
 *
 * DELIBERATELY not checked here: status, delivery, live platform count,
 * analytics snapshots, Sherloc history, platform mappings, reporting identity,
 * whether today falls inside the flight, or whether the campaign was ever
 * selected before. Every one of those is a state the page must RESOLVE after
 * selection and state truthfully — including "campaign not yet live". None is
 * a reason for a campaign to be missing from the list of campaigns.
 */
export const isCampaignAnalysisSelectableGovernedCampaign = (campaign) => {
  if (!campaign || typeof campaign !== "object") return false;
  if (isInternalReportingExcludedCampaign(campaign)) return false;
  return true;
};

const text = (value) => String(value ?? "").trim();

const flightStartKey = (option = {}) =>
  text(option.flightStartDate || option.flight_start_date).slice(0, 10);

const flightEndKey = (option = {}) =>
  text(option.flightEndDate || option.flight_end_date).slice(0, 10);

/*
 * Deterministic ordering, identical in both portals.
 *
 * `localeCompare` was the previous tiebreak and it is not a contract: its
 * result depends on the viewer's runtime locale, so two portals — or two
 * operators — could legitimately list the same advertiser's campaigns in
 * different orders. Flight start is the canonical order the backend already
 * returns the list in (`flight_start_date ASC`); the remaining keys exist only
 * to make ties total, and compare by code unit so the answer never depends on
 * where the browser thinks it is. Undated campaigns sort last rather than
 * first, so an unscheduled draft never leads the list.
 */
const compareByKey = (left, right) => {
  if (left === right) return 0;
  return left < right ? -1 : 1;
};

const compareOptionalDate = (left, right) => {
  if (left && right) return compareByKey(left, right);
  if (left) return -1;
  if (right) return 1;
  return 0;
};

export const orderCampaignAnalysisCampaignOptions = (options = []) =>
  (Array.isArray(options) ? options : [])
    .filter((option) => option && typeof option === "object")
    .slice()
    .sort((a, b) => {
      const byStart = compareOptionalDate(flightStartKey(a), flightStartKey(b));
      if (byStart !== 0) return byStart;
      const byEnd = compareOptionalDate(flightEndKey(a), flightEndKey(b));
      if (byEnd !== 0) return byEnd;
      const byName = compareByKey(text(a.name).toLowerCase(), text(b.name).toLowerCase());
      if (byName !== 0) return byName;
      return compareByKey(text(a.id), text(b.id));
    });

/*
 * The Campaign Analysis selector's campaign inventory.
 *
 * Identity is the canonical resolver's, unchanged: one campaign is one option,
 * keyed by immutable ids and exact normalized name keys. A governed option's id
 * is its media campaign id, never its display name — which is what keeps
 * "Never Stop Chasing" (44) and "Never Stop Chasing Incremental Markets" (50)
 * two campaigns rather than one, despite the shared prefix. Nothing here
 * matches names with includes()/startsWith(); the resolver's name keys are
 * whole-string and are only ever a dedupe fallback behind identity.
 */
export const buildCampaignAnalysisSelectorOptions = ({
  liveCampaigns = [],
  importedReportingCampaigns = [],
  mediaCampaigns = [],
  reportingContext = null,
} = {}) =>
  orderCampaignAnalysisCampaignOptions(
    resolveCanonicalCampaigns({
      liveCampaigns,
      importedReportingCampaigns,
      mediaCampaigns,
      reportingContext,
      governedCampaignOptionFilter: isCampaignAnalysisSelectableGovernedCampaign,
    })
  );

const campaignIdentityValues = (campaign = {}) =>
  [
    campaign.id,
    campaign.mediaCampaignId,
    campaign.media_campaign_id,
    campaign.reportingCampaignId,
    campaign.campaign_id,
    campaign.campaignId,
  ]
    .map(text)
    .filter(Boolean);

/*
 * Narrow the advertiser's governed campaigns to the SELECTED one.
 *
 * The page's flight window and its Live Platforms set were both derived from
 * the advertiser's whole approved-campaign list through a heuristic that scores
 * campaigns (countersigned, in-flight, has platforms) and keeps the winner.
 * With one campaign per advertiser that read as "the campaign". With two it is
 * a coin toss the selection does not participate in — Campaign 50 selected
 * would still have been shown Campaign 44's platforms and Campaign 44's flight,
 * because the same advertiser scored the same winner either way.
 *
 * When a campaign is explicitly selected, the campaign-scoped surfaces get
 * exactly that campaign's governed row, matched by immutable identity. Under
 * "Auto" the whole list is returned and the existing heuristic still decides —
 * that is what "Auto" means. A selection that matches nothing yields an empty
 * list rather than the advertiser's list, so a stale selection resolves to the
 * honest empty state instead of borrowing another campaign's plan.
 */
export const selectGovernedCampaignsForSelection = ({
  campaigns = [],
  selectedCampaignId = null,
  selectedCampaign = null,
} = {}) => {
  const list = (Array.isArray(campaigns) ? campaigns : []).filter(
    (campaign) => campaign && typeof campaign === "object"
  );
  const selectionId = text(selectedCampaignId);
  if (!selectionId || selectionId === "auto") return list;

  const wanted = new Set([
    selectionId,
    ...campaignIdentityValues(selectedCampaign || {}),
  ]);

  return list.filter((campaign) =>
    campaignIdentityValues(campaign).some((value) => wanted.has(value))
  );
};

/*
 * The governed media campaign id behind the current selection, or null under
 * "Auto". This is the identity every campaign-scoped read downstream keys on —
 * approved plan, flight window, platform scope — so it is resolved once, from
 * the option, and never re-derived from a display name.
 */
export const resolveSelectedGovernedCampaignId = ({
  selectedCampaignId = null,
  selectedCampaign = null,
} = {}) => {
  const selectionId = text(selectedCampaignId);
  if (!selectionId || selectionId === "auto") return null;
  return (
    text(selectedCampaign?.mediaCampaignId) ||
    text(selectedCampaign?.reportingCampaignId) ||
    selectionId ||
    null
  );
};
